import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ticket, Trophy } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { api } from "../../lib/api";
import { basarUrl, nok, soldPercent } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

// «Basaren vår» – vist på arrangør- og arrangementssida berre når arrangøren har open basar (eller nyleg trekt). Fail-soft.
export const BasarTeaser = ({ organizerId, organizerName, className = "" }) => {
  const { t } = useLang();
  const [teaser, setTeaser] = useState(null);
  useEffect(() => {
    if (!organizerId) return undefined;
    let alive = true;
    api.get(`/basar/organizers/${organizerId}/teaser`).then((res) => alive && setTeaser(res.data)).catch(() => alive && setTeaser(null));
    return () => {
      alive = false;
    };
  }, [organizerId]);
  if (!teaser?.enabled) return null;
  const open = teaser.basars || [];
  const drawn = teaser.drawn || [];
  const items = open.length ? open.slice(0, 2) : drawn.slice(0, 1);
  if (!items.length) return null;
  return (
    <Card className={`border-border bg-card ${className}`} data-testid="basar-teaser">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] shrink-0"><Ticket className="w-5 h-5" /></span>
            <div className="min-w-0">
              <p className="font-heading font-semibold">{open.length ? t("Basaren vår") : t("Basaren er trukket")}</p>
              <p className="text-xs text-muted-foreground truncate">{organizerName}</p>
            </div>
          </div>
          {open.length > 0 && <Badge variant="outline" className="shrink-0" data-testid="basar-teaser-count">{t("Loddsalg pågår")}</Badge>}
        </div>
        <div className="space-y-3">
          {items.map((b) => (
            <Link key={b.id} to={basarUrl(b.id)} className="block rounded-lg border border-border p-3 hover:bg-accent transition-colors duration-150" data-testid={`basar-teaser-item-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium leading-snug line-clamp-2">{b.name}</p>
                <span className="text-xs font-semibold tabular-nums shrink-0">{t("{amount}/lodd", { amount: nok(b.price) })}</span>
              </div>
              {b.status === "drawn" ? (
                <p className="text-xs text-muted-foreground mt-1.5 inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-[hsl(var(--warning))]" /> {t("Vinnerne er publisert")}</p>
              ) : (
                <div className="mt-2 space-y-1">
                  <Progress value={soldPercent(b.stats)} className="h-1.5 bg-[hsl(var(--warning))]/15 [&>div]:bg-[hsl(var(--warning))]" />
                  <p className="text-xs text-muted-foreground tabular-nums">{t("Solgt {sold} av {total}", { sold: b.stats?.sold ?? 0, total: b.stats?.total ?? 0 })}{b.prizes_count ? ` · ${b.prizes_count === 1 ? t("{n} premie", { n: 1 }) : t("{n} premier", { n: b.prizes_count })}` : ""}</p>
                </div>
              )}
            </Link>
          ))}
        </div>
        <Button asChild variant="secondary" className="w-full justify-center" data-testid="basar-teaser-link">
          <Link to={basarUrl(items[0].id)}>
            {open.length ? t("Kjøp lodd") : t("Se vinnerne")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
